import { DOCUMENT } from '@angular/common';
import { Injectable, computed, effect, inject, signal } from '@angular/core';

export type PortalSkin = 'technical-precision' | 'modern-premium';

export interface SkinOption {
  value: PortalSkin;
  label: string;
}

const SKIN_KEY = 'driveme.ui.skin.v2';
const DEFAULT_SKIN: PortalSkin = readPreferredSkin();

export const SKIN_OPTIONS: SkinOption[] = [
  { value: 'technical-precision', label: 'Bright' },
  { value: 'modern-premium', label: 'Dark' }
];

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly skinState = signal<PortalSkin>(readStoredSkin());

  readonly currentSkin = computed(() => this.skinState());
  readonly skins = SKIN_OPTIONS;

  constructor() {
    effect(() => {
      const skin = this.skinState();
      this.document.documentElement.dataset['skin'] = skin;
      localStorage.setItem(SKIN_KEY, skin);
    });
  }

  setSkin(skin: string): void {
    if (isPortalSkin(skin)) {
      this.skinState.set(skin);
    }
  }
}

function readStoredSkin(): PortalSkin {
  const storedSkin = localStorage.getItem(SKIN_KEY);
  return isPortalSkin(storedSkin) ? storedSkin : DEFAULT_SKIN;
}

function readPreferredSkin(): PortalSkin {
  const prefersDark = globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  return prefersDark ? 'modern-premium' : 'technical-precision';
}

function isPortalSkin(value: string | null): value is PortalSkin {
  return value === 'technical-precision' || value === 'modern-premium';
}
